export const brand = {
  identity: {
    name: "AI & Computer Services",
    shortName: "AICS",
    logoSrc: "/logo.png",
    logoAlt: "AI Computer Services and Networks",
    eyebrow: "AI + Computer Services",
    headline:
      "AI receptionist, AI chat, UniFi networks, and custom websites in Southern California.",
    subheadline:
      "Replace voicemail with an AI receptionist, put AI chat behind your docs and database, deploy Unifi-grade networks, and launch fast custom sites - not WordPress bloat.",
    loadingMessage: "Spinning up AICS",
    status: "Now booking installs",
    footerNote:
      "Tell us what you're building - we'll tailor AI, networking, and web installs to your timeline.",
  },
  story: {
    scrollHint: "Scroll through services",
    sections: [
      {
        kicker: "01 / Top seller",
        title: "AI receptionist",
        outcome: "Fewer missed calls, more booked work.",
        body: "Answers 24/7, qualifies leads, and books appointments - so every ring turns into captured business instead of voicemail.",
        bullets: [
          "After-hours coverage with scripts you control",
          "Calendar and CRM handoff when it matters",
        ],
        ctaLabel: "Explore receptionist",
        ctaPath: "/receptionist",
        accent: "primary",
      },
      {
        kicker: "02 / Private AI",
        title: "AI chat, RAG, and your DB",
        outcome: "Accurate answers from your docs - not generic fluff.",
        body: "A chat assistant grounded in your docs and data. Customers get answers now, not “we’ll get back to you” - without exposing secrets.",
        bullets: [
          "RAG over manuals, FAQs, and uploads",
          "Scoped so customers see answers - not raw credentials",
        ],
        ctaLabel: "See AI chat scope",
        ctaPath: "/ai-chat",
        accent: "secondary",
      },
      {
        kicker: "03 / Reliable Wi‑Fi",
        title: "Unifi network setup",
        outcome: "Coverage and throughput that hold up under load.",
        body: "Design, cabling, UniFi installs, VLANs where you need them, and tuning until coverage and speed stay solid for offices and homes.",
        bullets: [
          "Survey-driven AP placement",
          "Guest, staff, and IoT segmentation where needed",
        ],
        ctaLabel: "UniFi installs",
        ctaPath: "/unifi-networks",
        accent: "accent",
      },
      {
        kicker: "04 / Built for speed",
        title: "Custom websites - not WordPress",
        outcome: "Fast loads, clear story, stacks you own.",
        body: "Hand-built branded sites focused on performance and clarity - no plugin churn, no theme treadmill, just deployable HTML and stacks you actually own.",
        bullets: [
          "Performance-first static or lightweight stacks",
          "Deploy pipelines without theme lock-in",
        ],
        ctaLabel: "Custom sites",
        ctaPath: "/custom-sites",
        accent: "secondary",
      },
      {
        kicker: "05 / Built for your workflow",
        title: "Example deployments by business type",
        outcome:
          "Example workflows based on common small-business operations.",
        body: "These are illustrative scenarios - not client endorsements. Your discovery call determines what should be automated and what stays human.",
        layout: "useCases",
        accent: "primary",
        scenarios: [
          {
            category: "Owner-operated roofing contractor",
            headline: "Calls go to voicemail while the crew is on jobs",
            problem:
              "No office staff to answer estimate requests during the workday.",
            solution:
              "AI receptionist captures property address, urgency, and callback window; sends the owner a structured summary for follow-up.",
            outcome:
              "Fewer missed leads and faster callbacks without hiring a dispatcher.",
            serviceLabel: "AI receptionist",
            servicePath: "/receptionist",
            featured: true,
          },
          {
            category: "Home-and-garden retailer",
            headline: "Staff spend too much time on repeat product questions",
            problem:
              "Customers wait on hold for stock, returns, and seasonal availability answers.",
            solution:
              "Site chat grounded in FAQs, product sheets, and policy docs - with handoff when a question exceeds the knowledge base.",
            outcome:
              "Faster answers for shoppers; staff handle exceptions instead of repetitive calls.",
            serviceLabel: "AI chat",
            servicePath: "/ai-chat",
          },
          {
            category: "Professional-services office",
            headline: "Wi‑Fi and guest access are unreliable under daily load",
            problem:
              "Dropped connections and mixed guest/staff traffic disrupt client meetings and internal work.",
            solution:
              "UniFi survey, AP placement, segmented guest and staff networks, and throughput validation.",
            outcome:
              "Predictable coverage for staff devices and isolated guest access for visitors.",
            serviceLabel: "UniFi networks",
            servicePath: "/unifi-networks",
          },
          {
            category: "Mobile detailing business",
            headline: "No credible web presence to capture quote requests",
            problem:
              "Outdated or missing site; quote inquiries get lost in DMs and voicemail.",
            solution:
              "Fast, brand-faithful marketing site with clear service areas, pricing cues, and contact flows.",
            outcome:
              "Professional first impression and structured inbound requests - not theme-plugin churn.",
            serviceLabel: "Custom sites",
            servicePath: "/custom-sites",
            featured: true,
          },
        ],
        universal: {
          title: "Your workflow is the starting point",
          body: "From contractors and property teams to clinics, shops, and service operators, each rollout is configured around your call flow, knowledge base, network environment, and handoff rules. The discovery call determines what should be automated and what should stay human.",
        },
        ctaLabel: "Talk through your workflow",
        ctaPath: "/contact",
        secondaryLabel: "See receptionist scope",
        secondaryHref: "/receptionist",
      },
      {
        kicker: "06 / Next step",
        title: "Talk to us",
        outcome: "One contact across AI, network, and web.",
        body: "Planning a rollout? We’ll estimate receptionist hours, chat scope, UniFi work, or a launch site - and schedule installs to match your ops.",
        bullets: [
          "Ballpark estimates before deeper discovery",
          "Scheduling that fits how your team works",
        ],
        ctaLabel: "Get in touch",
        ctaPath: "/contact",
        accent: "primary",
      },
    ],
  },
  featureCards: [
    {
      kicker: "Top seller",
      title: "Receptionist",
      stat: "01",
      body: "Never miss a lead: AI answering, scripting, routing, and calendar hooks tuned to how you operate.",
      slug: "receptionist",
      ctaPath: "/receptionist",
      detail: {
        summary:
          "24/7 AI answering, lead qualification, and scheduling - so calls become booked work instead of voicemail.",
        bullets: [
          "After-hours coverage with brand-consistent scripts",
          "Calendar and CRM handoff rules you control",
          "Escalation paths to your team when it matters",
        ],
      },
      position: { x: "72vw", y: "22vh" },
      accent: "primary",
    },
    {
      kicker: "Private stack",
      title: "AI chat",
      stat: "02",
      body: "RAG over your manuals and FAQs, grounded answers, optional database-backed workflows for staff.",
      slug: "ai-chat",
      ctaPath: "/ai-chat",
      detail: {
        summary:
          "Private assistants grounded in your docs and data - accurate answers without exposing sensitive internals.",
        bullets: [
          "RAG over uploads, manuals, and site content",
          "Optional database-backed workflows for staff",
          "Scoped so customers see answers - not raw credentials",
        ],
      },
      position: { x: "80vw", y: "56vh" },
      accent: "secondary",
    },
    {
      kicker: "Solid signal",
      title: "UniFi nets",
      stat: "03",
      body: "AP placement, backbone switching, guest networks, monitoring - wired and wireless that holds up.",
      slug: "unifi-networks",
      ctaPath: "/unifi-networks",
      detail: {
        summary:
          "Design through tuning: cabling, UniFi stack, VLANs, guest Wi‑Fi, and monitoring that stays solid.",
        bullets: [
          "Survey-driven AP placement and backbone planning",
          "Guest, staff, and IoT segmentation where needed",
          "Performance validation before we call it done",
        ],
      },
      position: { x: "22vw", y: "66vh" },
      accent: "accent",
    },
    {
      kicker: "No templates",
      title: "Custom site",
      stat: "04",
      body: "Fast static or app-backed sites coded to match your brand - not a drag-and-drop theme.",
      slug: "custom-sites",
      ctaPath: "/custom-sites",
      detail: {
        summary:
          "Hand-built marketing and app surfaces focused on speed and clarity - no plugin treadmill.",
        bullets: [
          "Performance-first static stacks or lightweight apps",
          "Brand-faithful UI - not generic themes",
          "Deploy pipelines you actually own",
        ],
      },
      position: { x: "46vw", y: "84vh" },
      accent: "secondary",
    },
    {
      kicker: "Next step",
      title: "Talk to us",
      stat: "05",
      body: "Planning a rollout? Quote AI receptionist hours, chat scope, UniFi installs, or a launch site - we’ll estimate and schedule.",
      ctaPath: "/contact",
      detail: {
        summary:
          "Tell us what you’re rolling out - we’ll scope AI receptionist, chat, networking, and web work on your timeline.",
        bullets: [
          "Ballpark estimates before deeper discovery",
          "Scheduling installs and handoffs that match your ops",
          "One point of contact across AI, network, and web",
        ],
      },
      position: { x: "54vw", y: "40vh" },
      accent: "primary",
    },
  ],
  theme: {
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    colors: {
      background: "#0c0e12",
      backgroundAlt: "#16191f",
      surface: "rgba(18, 21, 26, 0.72)",
      surfaceStrong: "rgba(20, 24, 30, 0.9)",
      text: "#f5f7fa",
      muted: "#98a2b3",
      primary: "#4c8dff",
      secondary: "#dbe5ff",
      accent: "#4c8dff",
      grid: "#1f242c",
    },
  },
  scene: {
    camera: {
      position: [0, 0, 5],
      fov: 75,
      near: 0.1,
      far: 1000,
    },
    fog: {
      near: 10,
      far: 52,
    },
    heroObject: {
      radius: 1,
      detail: 1,
      metalness: 0.72,
      roughness: 0.18,
    },
    wireframe: {
      opacity: 0.28,
      activeOpacity: 0.82,
      scale: 1.025,
    },
    particles: {
      count: 650,
      spread: 16,
      size: 0.045,
      opacity: 0.76,
      floatAmplitude: 0.45,
      rotationSpeed: 0.045,
    },
    grid: {
      size: 22,
      divisions: 22,
      y: -2,
    },
    lights: {
      ambientIntensity: 0.46,
      directionalIntensity: 1.1,
      pointIntensity: 1.25,
      pointDistance: 12,
    },
  },
  animation: {
    introDuration: 1.35,
    introEase: "elastic.out(1, 0.5)",
    rotationSpeed: 0.42,
    mouseInfluence: 0.5,
    rotationLerp: 0.05,
    cameraFollowRange: 2.6,
    cameraLerp: 0.06,
    scrollLerp: 0.08,
    scrollRotation: 2.4,
    scrollTilt: 0.8,
    scrollCameraDepth: 2.2,
    scrollObjectLift: 0.85,
    jumpHeight: 2,
    jumpScale: 1.5,
  },
  layout: {
    /** "transparent" | "glass" - hero brand overlay (layer 1) background */
    heroOverlayStyle: "transparent",
  },
};

/** Current key - bump after a breaking rebrand so old presets do not shadow `brand`. */
export const BRAND_STORAGE_KEY = "threejs-gsap-starter:brand:v2";

/** Prior key; removed on load so stranded saves do not confuse users after a rebrand. */
const LEGACY_BRAND_STORAGE_KEYS = ["threejs-gsap-starter:brand-config"];

export function cloneBrandConfig(brandConfig = brand) {
  return JSON.parse(JSON.stringify(brandConfig));
}

export function mergeBrandConfig(baseConfig = brand, overrides = {}) {
  const output = cloneBrandConfig(baseConfig);

  Object.entries(overrides || {}).forEach(([key, value]) => {
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      output[key] &&
      typeof output[key] === "object" &&
      !Array.isArray(output[key])
    ) {
      output[key] = mergeBrandConfig(output[key], value);
    } else {
      output[key] = value;
    }
  });

  return output;
}

/** Slugs allowed in story CTAs (aligned with service card `slug` strings). */
const STORY_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/i;

/**
 * Valid internal path for story links: single-root path, no protocol tricks.
 * @param {unknown} raw
 * @returns {string | null}
 */
export function sanitizeStoryInternalPath(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s || !s.startsWith("/") || s.startsWith("//")) return null;
  if (s.includes("\\")) return null;
  const schemeIdx = s.indexOf(":");
  const slashIdx = s.indexOf("/", 1);
  if (schemeIdx !== -1 && (slashIdx === -1 || schemeIdx < slashIdx))
    return null;
  return s;
}

/**
 * Safe external href for story CTAs: https or mailto only.
 * @param {unknown} raw
 * @returns {string | null}
 */
export function sanitizeStoryExternalHref(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const lower = s.toLowerCase();
  if (
    lower.startsWith("javascript:") ||
    lower.startsWith("data:") ||
    lower.startsWith("vbscript:")
  )
    return null;
  try {
    const u = new URL(s);
    if (u.protocol === "https:") return u.toString();
    if (u.protocol === "mailto:") return u.toString();
    return null;
  } catch {
    return null;
  }
}

/**
 * @param {unknown} raw
 * @returns {string | null}
 */
export function sanitizeStorySlug(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s || !STORY_SLUG_PATTERN.test(s)) return null;
  return s;
}

/**
 * Primary story CTA: valid `ctaPath` → valid `ctaSlug` (`/services/...`) → safe `ctaHref`; else null.
 * @param {Record<string, unknown> | null | undefined} section
 * @returns {string | null}
 */
export function resolveStoryCtaUrl(section) {
  if (!section || typeof section !== "object") return null;
  const path = sanitizeStoryInternalPath(section.ctaPath);
  if (path) return path;
  const slug = sanitizeStorySlug(section.ctaSlug);
  if (slug) return `/services/${encodeURIComponent(slug)}`;
  const ext = sanitizeStoryExternalHref(section.ctaHref);
  if (ext) return ext;
  return null;
}

/** @param {Record<string, unknown> | null | undefined} section */
export function getStoryCtaHref(section) {
  return resolveStoryCtaUrl(section);
}

/**
 * Secondary link: leading `/` uses internal rules; otherwise https/mailto only.
 * @param {Record<string, unknown> | null | undefined} section
 * @returns {string | null}
 */
export function resolveStorySecondaryHref(section) {
  if (!section || typeof section !== "object") return null;
  const raw = section.secondaryHref;
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (s.startsWith("/")) return sanitizeStoryInternalPath(s);
  return sanitizeStoryExternalHref(s);
}

/** Resolve in-hero card link: `/contact` for CTAs, `/services/:slug` for service cards. */
export function getCardHref(card) {
  if (!card) return "#";
  if (card.ctaPath) return card.ctaPath;
  if (card.slug) return `/services/${encodeURIComponent(card.slug)}`;
  return "#";
}

/** Service detail data keyed by `slug` (excludes CTAs like `/contact`). */
export function getServiceBySlug(slug, brandConfig) {
  const list = brandConfig?.featureCards ?? [];
  return list.find((c) => c.slug === slug) ?? null;
}

export function getContactCard(brandConfig) {
  const list = brandConfig?.featureCards ?? [];
  return list.find((c) => c.ctaPath === "/contact") ?? null;
}

export function getBrandCssVariables(brandConfig = brand) {
  return {
    "--brand-font-family": brandConfig.theme.fontFamily,
    "--brand-color-bg": brandConfig.theme.colors.background,
    "--brand-color-bg-alt": brandConfig.theme.colors.backgroundAlt,
    "--brand-color-surface": brandConfig.theme.colors.surface,
    "--brand-color-surface-strong": brandConfig.theme.colors.surfaceStrong,
    "--brand-color-text": brandConfig.theme.colors.text,
    "--brand-color-muted": brandConfig.theme.colors.muted,
    "--brand-color-primary": brandConfig.theme.colors.primary,
    "--brand-color-secondary": brandConfig.theme.colors.secondary,
    "--brand-color-accent": brandConfig.theme.colors.accent,
    "--brand-color-grid": brandConfig.theme.colors.grid,
  };
}

export const brandCssVariables = getBrandCssVariables();

export function applyBrandCssVariables(
  brandConfig = brand,
  target = document.documentElement,
) {
  if (brandConfig?.style?.setProperty) {
    target = brandConfig;
    brandConfig = brand;
  }

  Object.entries(getBrandCssVariables(brandConfig)).forEach(([name, value]) => {
    target.style.setProperty(name, value);
  });
}

export function loadStoredBrandConfig(storage = globalThis.localStorage) {
  if (!storage) return cloneBrandConfig();

  try {
    LEGACY_BRAND_STORAGE_KEYS.forEach((key) => storage.removeItem(key));

    const storedConfig = storage.getItem(BRAND_STORAGE_KEY);
    return storedConfig
      ? mergeBrandConfig(brand, JSON.parse(storedConfig))
      : cloneBrandConfig();
  } catch (error) {
    console.warn("Unable to load stored brand settings.", error);
    return cloneBrandConfig();
  }
}

export function saveBrandConfig(
  brandConfig,
  storage = globalThis.localStorage,
) {
  if (!storage) return;
  storage.setItem(BRAND_STORAGE_KEY, JSON.stringify(brandConfig));
}

export function resetStoredBrandConfig(storage = globalThis.localStorage) {
  if (!storage) return;
  storage.removeItem(BRAND_STORAGE_KEY);
}
